import { z } from 'zod';
import type { TipoControl } from './tipos.js';

/**
 * Módulo de facturación del OPERADOR de la plataforma. El operador cobra a cada
 * empresa por las visitas de control (tarifa por empresa × tipo de control) y
 * le paga a cada profesional un honorario por atención; la diferencia es su
 * ganancia. La unidad facturable es la VISITA atendida (un `control`, cualquier
 * resultado), excluyendo las rectificativas (HU-14: corrigen, no agregan visita).
 *
 * El dinero viaja SIEMPRE en centavos enteros (ARS) para evitar errores de
 * coma flotante (IEEE-754); se formatea a pesos solo en el borde (UI) con
 * `formatearMonedaArs`.
 */

const fechaIso = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha (AAAA-MM-DD) inválida');

export const esquemaTarifaEmpresa = z.object({
  empresaId: z.string().min(1),
  // Antes z.enum(TIPOS_CONTROL) — el Operador administra sus propios tramos
  // ahora, así que se valida contra la tabla real en el servidor, no un enum.
  tipoControl: z.string().min(1),
  montoCentavos: z.number().int().min(0).max(1_000_000_000),
  vigenteDesde: fechaIso,
});

export const esquemaHonorarioMedico = z.object({
  medicoId: z.string().min(1),
  montoCentavos: z.number().int().min(0).max(1_000_000_000),
  vigenteDesde: fechaIso,
});

export const esquemaPeriodo = z
  .object({ desde: fechaIso, hasta: fechaIso })
  .superRefine((p, ctx) => {
    if (p.desde > p.hasta)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['hasta'],
        message: 'La fecha "hasta" no puede ser anterior a "desde"',
      });
  });

export type TarifaEmpresaInput = z.infer<typeof esquemaTarifaEmpresa>;
export type HonorarioMedicoInput = z.infer<typeof esquemaHonorarioMedico>;
export type Periodo = z.infer<typeof esquemaPeriodo>;

export interface TarifaEmpresaDTO {
  empresaId: string;
  empresaNombre: string;
  tipoControl: TipoControl;
  tipoControlNombre: string;
  montoCentavos: number | null;
  vigenteDesde: string | null;
}

export interface HonorarioMedicoDTO {
  medicoId: string;
  medicoNombre: string;
  montoCentavos: number | null;
  vigenteDesde: string | null;
}

export interface ResumenEmpresaDTO {
  empresaId: string;
  empresaNombre: string;
  visitas: number;
  visitasSinTarifa: number;
  totalCentavos: number;
}

export interface ResumenMedicoDTO {
  medicoId: string;
  medicoNombre: string;
  visitas: number;
  visitasSinHonorario: number;
  totalCentavos: number;
}

export interface VisitaSinValorDTO {
  controlId: string;
  fechaControl: string;
  empresaNombre: string;
  tipoControl: TipoControl;
  tipoControlNombre: string;
  medicoNombre: string;
}

export interface ReporteFacturacionDTO {
  periodo: { desde: string; hasta: string };
  visitasFacturables: number;
  totalACobrarCentavos: number;
  totalAPagarCentavos: number;
  gananciaCentavos: number;
  porEmpresa: ResumenEmpresaDTO[];
  porMedico: ResumenMedicoDTO[];
  visitasSinTarifa: VisitaSinValorDTO[];
  visitasSinHonorario: VisitaSinValorDTO[];
}
export interface VisitaFacturacionPortalDTO {
  controlId: string;
  fechaControl: string;
  tipoControl: TipoControl;
  tipoControlNombre: string;
  trabajadorNombre: string;
  trabajadorDni: string;
  trabajadorLegajo: string | null;
  constanciaCodigo: string | null;
  montoCentavos: number | null;
  tieneTarifa: boolean;
}

export interface ReporteFacturacionPortalDTO {
  periodo: { desde: string; hasta: string };
  resumen: {
    totalControles: number;
    totalACobrarCentavos: number;
    totalSinTarifa: number;
  };
  visitas: VisitaFacturacionPortalDTO[];
}

export interface DetalleControlHonorarioDTO {
  controlId: string;
  fechaControl: string;
  tipoControl: TipoControl;
  tipoControlNombre: string;
  trabajadorNombre: string;
  empresaNombre: string;
  montoCentavos: number | null;
}

export interface ReporteHonorariosMedicoDTO {
  periodo: { desde: string; hasta: string };
  totalGanadoCentavos: number;
  estadisticas: {
    totalVisitas: number;
    visitasConHonorario: number;
    visitasSinHonorario: number;
  };
  controles: DetalleControlHonorarioDTO[];
}

/** Formatea centavos ARS a pesos ($ 12.345,67). Único punto de salida del dinero. */
export function formatearMonedaArs(centavos: number): string {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(
    centavos / 100,
  );
}

/**
 * Comprobantes de facturación (ADR-0010). El operador (CLIENTE) emite una
 * factura a cada EMPRESA por las visitas del período; cada MÉDICO emite una
 * factura al operador por sus honorarios. Demo coherente con el resto del
 * prototipo: número correlativo, estados y PDF descargable, SIN AFIP/IVA/CAE.
 */

export type TipoFactura = 'A_EMPRESA' | 'DE_MEDICO';
export type EstadoFactura = 'EMITIDA' | 'COBRADA' | 'PAGADA' | 'ANULADA';

export const esquemaEmitirFacturaEmpresa = z
  .object({ empresaId: z.string().min(1), desde: fechaIso, hasta: fechaIso })
  .superRefine((p, ctx) => {
    if (p.desde > p.hasta)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['hasta'],
        message: 'La fecha "hasta" no puede ser anterior a "desde"',
      });
  });

/** El médico se identifica por el token; sólo necesita el período. */
export const esquemaEmitirFacturaMedico = esquemaPeriodo;

export type EmitirFacturaEmpresaInput = z.infer<typeof esquemaEmitirFacturaEmpresa>;
export type EmitirFacturaMedicoInput = z.infer<typeof esquemaEmitirFacturaMedico>;

export interface LineaFacturaDTO {
  concepto: string;
  tipoControl: TipoControl | null;
  cantidad: number;
  unitarioCentavos: number;
  subtotalCentavos: number;
}

export interface FacturaDTO {
  id: string;
  numero: string;
  tipo: TipoFactura;
  estado: EstadoFactura;
  /** Empresa facturada (A_EMPRESA) o médico emisor (DE_MEDICO). */
  contraparteNombre: string;
  periodo: { desde: string; hasta: string };
  visitas: number;
  totalCentavos: number;
  lineas: LineaFacturaDTO[];
  emitidaEn: string;
  saldadaEn: string | null;
}

export const ETIQUETA_ESTADO_FACTURA: Record<EstadoFactura, string> = {
  EMITIDA: 'Emitida',
  COBRADA: 'Cobrada',
  PAGADA: 'Pagada',
  ANULADA: 'Anulada',
};

/** Color de insignia por estado (mapea a las clases .insignia del front). */
export const COLOR_ESTADO_FACTURA: Record<EstadoFactura, 'verde' | 'ambar' | 'gris'> = {
  EMITIDA: 'ambar',
  COBRADA: 'verde',
  PAGADA: 'verde',
  ANULADA: 'gris',
};

